import { useEffect, useState } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import api from "../lib/api";
import CategoryCard from "../components/CategoryCard";

/**
 * Categories page.
 *
 * Robust rendering pipeline:
 *   status = "loading"  → skeleton grid (never shows an empty area silently)
 *   status = "error"    → visible error message + retry button
 *   status = "empty"    → friendly "no categories yet" state (API returned [])
 *   status = "ready"    → the real grid
 *
 * Race-condition safe: an AbortController + `alive` flag prevent a slow response
 * from overwriting a newer one when the effect re-runs (e.g. React StrictMode
 * double-invoke in dev, or a fast component remount).
 */
export default function Categories() {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  // `reloadKey` bumps to force a refetch on retry button click
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    // Reset only when we actually start a new fetch — never mid-render.
    setStatus("loading");
    setError(null);

    (async () => {
      try {
        const r = await api.get("/categories", { signal: controller.signal });
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setData(list);
        setStatus(list.length === 0 ? "empty" : "ready");
      } catch (err) {
        if (!alive || controller.signal.aborted) return;
        // Axios cancellation shouldn't surface as an error
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error("[Categories] failed to load categories:", err);
        setError(err?.message || "Erreur réseau");
        setStatus("error");
      }
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [reloadKey]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="categories-page">
      <div className="text-xs uppercase tracking-widest text-gold-700 font-mono font-semibold mb-3">Catalogue</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold text-ink-900">Toutes les catégories</h1>
      <p className="text-ink-600 mt-3 max-w-2xl">
        Chaque catégorie regroupe plusieurs certifications, du niveau débutant à avancé.
        Survolez chaque carte pour découvrir l'univers visuel propre à chaque thématique.
      </p>

      {/* LOADING — skeletons keep the layout stable and signal activity */}
      {status === "loading" && (
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-10"
          data-testid="categories-loading"
          aria-busy="true"
          aria-live="polite"
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={`skel-${i}`}
              className="rounded-2xl border border-ink-200 bg-white overflow-hidden"
              data-testid="category-skeleton"
            >
              <div className="aspect-[16/10] bg-ink-100 animate-pulse" />
              <div className="p-5 space-y-3">
                <div className="h-3 w-24 bg-ink-100 rounded animate-pulse" />
                <div className="h-6 w-3/4 bg-ink-100 rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-ink-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ERROR — clearly visible instead of a silent blank grid */}
      {status === "error" && (
        <div
          className="mt-10 rounded-2xl border border-danger/30 bg-danger/5 p-6 text-center"
          data-testid="categories-error"
          role="alert"
        >
          <div className="w-12 h-12 rounded-full bg-danger/10 flex items-center justify-center mx-auto mb-3">
            <AlertCircle className="w-6 h-6 text-danger" />
          </div>
          <h2 className="font-display text-xl font-bold text-ink-900">Impossible de charger les catégories</h2>
          <p className="text-ink-600 text-sm mt-1">
            {error ? String(error) : "Une erreur réseau est survenue."} Vérifiez votre connexion, puis réessayez.
          </p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gold-500 text-white font-medium hover:bg-gold-600"
            data-testid="categories-retry"
          >
            <RefreshCw className="w-4 h-4" /> Réessayer
          </button>
        </div>
      )}

      {/* EMPTY — never happens in prod (seed provides 8) but handled explicitly */}
      {status === "empty" && (
        <div
          className="mt-10 rounded-2xl border border-ink-200 bg-ink-50 p-8 text-center text-ink-600"
          data-testid="categories-empty"
        >
          Aucune catégorie n'est disponible pour le moment. Revenez très bientôt !
        </div>
      )}

      {/* READY — the real grid */}
      {status === "ready" && (
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-10"
          data-testid="categories-grid"
        >
          {data.map((c, i) => (
            <CategoryCard
              key={c.id ?? c.slug ?? `cat-${i}`}
              category={c}
              index={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}
